import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { meQuery } from '../api';
import { SettingHistoryDrawer } from '../settings/history';
import { formatPerShare } from '../settings/model';
import { canOpenArea, type SettingsAreaId } from '../settings/nav';
import { ChangedLine, SettingFallback, useSetting } from '../settings/parts';
import { Card, formatDate, formatCount, plural } from '../ui';
import s from './Home.module.css';

const FMV = 'values.fmvCents';
const WINDOW = 'election.windowDays';
const REMINDERS = 'election.reminderDaysBefore';

/** "Reminders with 3 days left and on the last day". */
export function remindersText(value: unknown): string {
  const days = Array.isArray(value)
    ? value.filter((d): d is number => typeof d === 'number').sort((a, b) => b - a)
    : [];
  if (days.length === 0) return 'No reminders';
  const parts = days.map((d) =>
    d === 0 ? 'on the last day' : `with ${plural(d, 'day', 'days')} left`,
  );
  return `Reminders ${parts.join(' and ')}`;
}

const WHERE: Readonly<Record<'values' | 'elections-letters', { href: string; label: string }>> = {
  values: { href: '/settings/values', label: 'Settings › Values' },
  'elections-letters': {
    href: '/settings/elections-letters',
    label: 'Settings › Elections and letters',
  },
};

/**
 * A value no one has set yet: greyed, with the way to where it's set for someone who can open it
 * (DESIGN-SPEC §5 "Not set yet", APP-093).
 */
function NotSet({
  area,
  canOpen,
}: {
  area: Extract<SettingsAreaId, 'values' | 'elections-letters'>;
  canOpen: boolean;
}): ReactElement {
  return (
    <>
      <dd className={s.valueBig}>
        <span className={s.muted}>Not set</span>
      </dd>
      <dd>
        <span className={s.metaLine}>
          {canOpen ? (
            <Link href={WHERE[area].href} className={s.strongLink}>
              Set it in {WHERE[area].label}
            </Link>
          ) : (
            `It’s set in ${WHERE[area].label}.`
          )}
        </span>
      </dd>
    </>
  );
}

/**
 * The values every award uses today (SA1 "Values in use"): the FMV and the election window.
 * Day-one facts (DESIGN-SPEC §5): they show before any award arrives.
 */
export function ValuesInUseCard(): ReactElement | null {
  const fmv = useSetting(FMV);
  const electionWindow = useSetting(WINDOW, 'PAA');
  const reminders = useSetting(REMINDERS, 'PAA');
  const [history, setHistory] = useState<'fmv' | 'window' | null>(null);
  const me = useQuery(meQuery).data;
  const canOpen = (area: SettingsAreaId): boolean => me !== undefined && canOpenArea(me, area);
  if (fmv.status === 'hidden' && electionWindow.status === 'hidden') return null;
  return (
    <Card
      title="Values in use"
      titleSize="sm"
      help={{
        shows: 'The FMV and the election window every award uses today, and who last changed them.',
        canDo: 'Open History to see earlier values. Owners change them in Settings.',
      }}
    >
      <dl className={s.valuesInUse}>
        <div>
          <dt>FMV per share</dt>
          {fmv.status !== 'ready' ? (
            <dd>
              <SettingFallback state={fmv} />
            </dd>
          ) : typeof fmv.view.value !== 'number' ? (
            <NotSet area="values" canOpen={canOpen('values')} />
          ) : (
            <>
              <dd className={s.valueBig}>{formatPerShare(fmv.view.value)}</dd>
              {/* Each fact on its own line: no separator left hanging at a line's end (HOME2). */}
              <dd>
                {fmv.view.effectiveFrom !== null && (
                  <span className={s.metaLine}>Effective {formatDate(fmv.view.effectiveFrom)}</span>
                )}
                <ChangedLine
                  change={fmv.view.lastChanged}
                  onHistory={() => {
                    setHistory('fmv');
                  }}
                />
              </dd>
            </>
          )}
        </div>
        <div>
          <dt>Election window</dt>
          {electionWindow.status !== 'ready' ? (
            <dd>
              <SettingFallback state={electionWindow} />
            </dd>
          ) : typeof electionWindow.view.value !== 'number' ? (
            <NotSet area="elections-letters" canOpen={canOpen('elections-letters')} />
          ) : (
            <>
              <dd className={s.valueBig}>{plural(electionWindow.view.value, 'day', 'days')}</dd>
              <dd>
                {reminders.status === 'ready' && (
                  <span className={s.metaLine}>{remindersText(reminders.view.value)}</span>
                )}
                <ChangedLine
                  change={electionWindow.view.lastChanged}
                  onHistory={() => {
                    setHistory('window');
                  }}
                />
              </dd>
            </>
          )}
        </div>
      </dl>
      <SettingHistoryDrawer
        isOpen={history === 'fmv'}
        onOpenChange={(open) => {
          if (!open) setHistory(null);
        }}
        settingKey={FMV}
        title="FMV per share"
        subject="History · Values in use"
        format={(v) => (typeof v === 'number' ? formatPerShare(v) : 'Not set')}
        numeric={(v) => (typeof v === 'number' ? v : null)}
        formatNumber={(v) => formatPerShare(v)}
      />
      <SettingHistoryDrawer
        isOpen={history === 'window'}
        onOpenChange={(open) => {
          if (!open) setHistory(null);
        }}
        settingKey={WINDOW}
        scope="PAA"
        title="Election window"
        subject="History · Values in use"
        format={(v) => (typeof v === 'number' ? plural(v, 'day', 'days') : 'Not set')}
        numeric={(v) => (typeof v === 'number' ? v : null)}
        formatNumber={(v) => `${formatCount(v)} days`}
      />
    </Card>
  );
}
